/* Created by Interactive 63 - https://interactive63.com */
/**
 * A pair of number fields for X and Y values. Scale adds a link toggle that keeps the two
 * values proportional while one is edited.
 */
import type { Vec2 } from '../../../../core/model/types';
import { h } from '../../../ui/dom';
import { NumberInput } from './number-input';

export interface Vec2InputOptions {
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  /** Adds a link toggle that keeps X and Y proportional. */
  linkable?: boolean;
  scrubKey?: string;
  /** Called with the changed component(s). */
  onChange: (value: Partial<Vec2>, coalesceKey?: string) => void;
}

export class Vec2Input {
  readonly el: HTMLElement;
  private readonly x: NumberInput;
  private readonly y: NumberInput;
  private readonly link: HTMLButtonElement | null;
  private value: { x: number | null; y: number | null } = { x: null, y: null };

  /** Builds the two fields and, for scale, the link toggle. */
  constructor(private readonly options: Vec2InputOptions) {
    const shared = { unit: options.unit, min: options.min, max: options.max, step: options.step };
    this.x = new NumberInput({
      ...shared,
      label: 'X',
      scrubKey: options.scrubKey,
      onChange: (x, key) => {
        this.changed('x', x, key);
      },
    });
    this.y = new NumberInput({
      ...shared,
      label: 'Y',
      scrubKey: options.scrubKey,
      onChange: (y, key) => {
        this.changed('y', y, key);
      },
    });
    this.link = options.linkable
      ? h(
          'button',
          {
            type: 'button',
            class: 'link-toggle',
            'aria-pressed': 'true',
            'aria-label': 'Keep proportions',
            title: 'Keep proportions',
          },
          '⛓',
        )
      : null;
    this.link?.addEventListener('click', () => {
      const on = this.link?.getAttribute('aria-pressed') !== 'true';
      this.link?.setAttribute('aria-pressed', String(on));
    });
    this.el = h('span', { class: 'vec2-input' }, this.x.el, this.y.el, this.link);
  }

  /** Shows values; null components display as "Mixed". */
  setValue(value: { x: number | null; y: number | null }, force = false): void {
    this.value = value;
    this.x.setValue(value.x, force);
    this.y.setValue(value.y, force);
  }

  /** Makes both fields read-only. */
  setReadOnly(readOnly: boolean): void {
    this.x.setReadOnly(readOnly);
    this.y.setReadOnly(readOnly);
    if (this.link) this.link.disabled = readOnly;
  }

  /** Reports a changed component, adjusting the other one when proportions are linked. */
  private changed(axis: 'x' | 'y', next: number, key?: string): void {
    const linked = this.link?.getAttribute('aria-pressed') === 'true';
    const other = axis === 'x' ? 'y' : 'x';
    const here = this.value[axis];
    const there = this.value[other];
    if (linked && here !== null && there !== null) {
      const partner = here === 0 ? next : (there / here) * next;
      const rounded = Number(partner.toFixed(4));
      this.options.onChange({ [axis]: next, [other]: rounded }, key);
      return;
    }
    this.options.onChange({ [axis]: next }, key);
  }
}
